import { useState, useEffect } from 'react';

import { SupervisedUserCircleOutlined } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Stack, Tooltip } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import Button from 'domains/core/components/Button';
import DropdownButton from 'domains/core/components/DropdownButton';
import { DropdownButtonOption } from 'domains/core/components/DropdownButton/DropdownButton';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import { Segment } from 'domains/segments/types';
import { getSegmentCreatePath } from 'domains/segments/utils';
import MutationKeys from 'hooks/mutations/keys';
import useContacts from 'hooks/queries/useContacts';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useDebounce from 'hooks/useDebounce';
import useMutationStatus from 'hooks/useMutationStatus';
import useSegmentQueryManager from 'hooks/useSegmentQueryManager';
import { MutationStatus, SortDirection } from 'models/enums';
import { Folder } from 'models/types';
import { SEARCH_DEBOUNCE_MS } from 'utils';

import SegmentsTable from '../SegmentsTable';

import type { SegmentsTablePaginationProps } from '../SegmentsTable';

export type Props = {
    createButtonDropdownOptions?: DropdownButtonOption[];
    isUserOnRootLevel: boolean;
    segmentsQuery: UseQueryResult<(Segment | Folder)[] | undefined, Error>;
    isArchivedView?: boolean;
    onToggleArchivedView?: () => void;
    searchTerm?: string;
    onSearchTermChange?: (value: string) => void;
    segmentsPagination?: SegmentsTablePaginationProps;
    segmentsTotalCount?: number;
    onSegmentSort?: (sortColumn: string, direction: SortDirection) => void;
    manualSegmentSortBy?: boolean;
};

const SegmentsPageLayout = ({
    createButtonDropdownOptions,
    isUserOnRootLevel,
    segmentsQuery,
    isArchivedView = false,
    onToggleArchivedView,
    searchTerm,
    onSearchTermChange,
    segmentsPagination,
    segmentsTotalCount,
    onSegmentSort,
    manualSegmentSortBy = false,
}: Props) => {
    const history = useHistory();
    const { folderId: parentFolderId } = useSegmentQueryManager();
    const { canManageAudiences, canUploadContacts } = useAccessCentrePermissions();

    const [searchInputValue, setSearchInputValue] = useState(searchTerm ?? '');
    const debouncedSearch = useDebounce(searchInputValue, SEARCH_DEBOUNCE_MS);

    // Sync local input when parent clears search externally (e.g. folder navigation)
    useEffect(() => {
        if (!searchTerm && searchInputValue) setSearchInputValue('');
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onSearchTermChange?.(debouncedSearch);
    }, [debouncedSearch, onSearchTermChange]);

    const {
        data: contacts,
        error: contactsError,
        isError: isContactsError,
        isSuccess: isContactsSuccess,
    } = useContacts({ options: { limit: '1' } });

    const contactsFileUploadProps = useContactsFileUploadProps();

    const createAudienceStatus = useMutationStatus(MutationKeys.CREATE_AUDIENCE);
    const createSegmentStatus = useMutationStatus(MutationKeys.CREATE_SEGMENT);
    const createFolderStatus = useMutationStatus(MutationKeys.CREATE_SEGMENT_FOLDER);

    const { data: segmentsData, error, isError, isLoading, isRefetching, isSuccess } = segmentsQuery;

    const isEmpty = isArchivedView
        ? segmentsTotalCount !== undefined
            ? isSuccess && segmentsTotalCount === 0
            : isSuccess && !segmentsData?.length
        : isContactsSuccess && !contacts?.length;

    const getHeaderAction = () => {
        const icon = <SupervisedUserCircleOutlined />;
        if (isEmpty || isArchivedView || !canManageAudiences) return;

        if (!!createButtonDropdownOptions && isUserOnRootLevel) {
            return (
                <DropdownButton
                    ariaLabel="choose what to create"
                    data-testid="audience-create-button-dropdown"
                    icon={icon}
                    label="Create"
                    options={createButtonDropdownOptions}
                    size="medium"
                    variant="outlined"
                />
            );
        }

        return (
            <Button
                data-testid="audience-create-button"
                endIcon={icon}
                onClick={() => history.push(getSegmentCreatePath(parentFolderId))}
                variant="outlined"
            >
                Create
            </Button>
        );
    };

    const renderPageBody = () => {
        if (isEmpty && isUserOnRootLevel) {
            return (
                <EmptyStateLayout
                    caption={isArchivedView ? 'archived audience' : 'audience'}
                    header={isArchivedView ? 'no archived audiences' : 'build your audience'}
                    guidingText={
                        isArchivedView
                            ? "You don't have any archived audiences."
                            : 'Once your contacts are uploaded, start segmenting your contacts into audiences to reach the most relevant recipients'
                    }
                >
                    {!isArchivedView && canUploadContacts && (
                        <>
                            <ContactsUploadDialog {...contactsFileUploadProps} />
                            <FileUploadButton {...contactsFileUploadProps} />
                        </>
                    )}
                </EmptyStateLayout>
            );
        }

        return (
            <SegmentsTable
                segmentsQuery={segmentsQuery}
                isNestedContent={!isUserOnRootLevel}
                searchTerm={debouncedSearch}
                pagination={segmentsPagination}
                totalSegmentCount={segmentsTotalCount}
                onSegmentSort={onSegmentSort}
                manualSegmentSortBy={manualSegmentSortBy}
            />
        );
    };

    const renderHeaderOthers = () => {
        if (!onToggleArchivedView && !onSearchTermChange) return;

        const isControlsDisabled = isLoading || isRefetching;

        return (
            <Stack direction="row" spacing={0} alignItems="center">
                {onToggleArchivedView && (
                    <Tooltip
                        key={`audience-archive-tooltip-${isArchivedView}`}
                        title={isControlsDisabled ? '' : isArchivedView ? 'Original audience' : 'Archive list'}
                    >
                        <span>
                            <Button
                                onClick={onToggleArchivedView}
                                disabled={isControlsDisabled}
                                data-testid="archive-toggle-button"
                                sx={{ minWidth: 'auto', ml: -2 }} // Move icon further left
                            >
                                {isArchivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {onSearchTermChange && (
                    <SearchField
                        placeholder="Search audiences..."
                        value={searchInputValue}
                        onChange={setSearchInputValue}
                        disabled={isControlsDisabled}
                        dataTestId="audience-search-input"
                    />
                )}
            </Stack>
        );
    };

    return (
        <PageLayout
            header={isArchivedView ? 'archived audiences' : 'audiences'}
            headerIcon={<SupervisedUserCircleOutlined fontSize="inherit" />}
            headerAction={getHeaderAction()}
            headerOthers={renderHeaderOthers()}
        >
            {(createAudienceStatus === MutationStatus.SUCCESS || createSegmentStatus === MutationStatus.SUCCESS) && (
                <SuccessSnackbar successMessage="Audience saved successfully." />
            )}
            {createFolderStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Folder created successfully." />
            )}
            {isContactsError && <ErrorSnackbar errorMessage={contactsError?.message} />}
            {isError && <ErrorSnackbar errorMessage={error?.message} />}
            {(isLoading || isSuccess) && renderPageBody()}
        </PageLayout>
    );
};

export default SegmentsPageLayout;
